import './PrintDecorComponent.scss';

import React from 'react';
import type {
  IDecorMethodRecord,
  IPrintTypeRecord,
} from '@/common/interfaces/print-decor.interface';

import Popper from '@/components/Popper/Popper';
import { getImageExtension } from '@/utilities/CommonUtils.misc';
import { getUniqueId } from '@/utilities/CommonUtils';

interface PrintDecorComponentProps {
  className?: string;
  printTypeList: IPrintTypeRecord[];
  decorMethodList: IDecorMethodRecord[];
  isDefaultPrintType?: boolean;
  isDefaultDecoratingInfo?: boolean;
}

/**
 * Component to render the print type or decor method with variant and it's image.
 */
const PrintDecorComponent: React.FC<PrintDecorComponentProps> = ({
  ...props
}: PrintDecorComponentProps) => {
  const { className, printTypeList, decorMethodList, isDefaultPrintType, isDefaultDecoratingInfo } =
    props;
  /**
   * Method to render the popover content.
   *
   * @param imageSrc - The image source
   */
  const renderPopover = (imageSrc: string) => (
    <div className="print-type-popover">
      <img
        src={imageSrc}
        alt="Print Type"
        className={`pop-over-image ${getImageExtension(imageSrc)}`}
      />
    </div>
  );

  return (
    <div className={`print-type-wrapper ${className}`}>
      <div className="print-type-list">
        {printTypeList && printTypeList.length > 0 && (
          <>
            {printTypeList.map((printType) => (
              <div key={`${getUniqueId()}${printType.id}`} className="print-type-item">
                <Popper
                  content={renderPopover(printType.imageSrc)}
                  placement="top"
                  overlayClassName="print-type-image-popper"
                >
                  <div className="print-type-image">
                    <img src={printType.sImageSrc} alt={printType.type} />
                  </div>
                </Popper>
                <span className="types-name">
                  {printType.type}
                  {printType.variant && !isDefaultPrintType && <>:</>}
                </span>
                {!isDefaultPrintType && <span className="types-name">{printType.variant}</span>}
              </div>
            ))}
          </>
        )}

        {/* Decorating Methods */}
        {decorMethodList && decorMethodList.length > 0 && (
          <>
            {decorMethodList.map((decorMethod) => (
              <div key={`${getUniqueId()}${decorMethod.id}`} className="print-type-item">
                <Popper
                  content={renderPopover(decorMethod.imageSrc)}
                  placement="top"
                  overlayClassName="print-type-image-popper decorating-popover"
                >
                  <div className="print-type-image">
                    <img src={decorMethod.sImageSrc} alt={decorMethod.type} />
                  </div>
                </Popper>
                <span className="types-name">
                  {decorMethod.type}
                  {decorMethod.location && !isDefaultDecoratingInfo && <>:</>}
                </span>
                {decorMethod.location && !isDefaultDecoratingInfo && (
                  <span className="types-name">{decorMethod.location}</span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

PrintDecorComponent.displayName = 'PrintDecorComponent';

export default PrintDecorComponent;
